import React, { FunctionComponent, useEffect } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import { Text, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { TermLink } from '../TermLink'
import {
  CheckoutState,
  setConfirmTermsViewed,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCreditScoreDisclosureDocumentId,
  useIsConsumerLendingAutopayEnabled,
  useIsPaymonthlySUPOrder,
  useLoanAgreementDocumentId,
  usePaymentAuthorisationDocumentId,
} from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import styles from './style.module.css'
import { useMoney } from '../Money/useMoney'

interface TermsProps {
  setDocumentIdToShow: (documentId: string, isTilDisclosure?: boolean) => void
}

export const Terms: FunctionComponent<TermsProps> = ({ setDocumentIdToShow }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const state: CheckoutState = useCheckoutState()
  const autopayEnabled = useIsConsumerLendingAutopayEnabled()
  const paymentAuthorisationDocumentId = usePaymentAuthorisationDocumentId()
  const loanAgreementDocumentId = useLoanAgreementDocumentId()
  const creditScoreDisclosureDocumentId = useCreditScoreDisclosureDocumentId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()

  const isSupPMOrder = useIsPaymonthlySUPOrder()
  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount

  const { formatMoney } = useMoney({
    locale,
    currency: orderAmount?.currency,
  })

  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const components = {
    autoPayAuthorization: (
      <TermLink
        key='til-autopay-authorisation'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='auto-payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_PAYMENT_AUTHORISATION_LINK)
          setDocumentIdToShow(paymentAuthorisationDocumentId)
        }}
      />
    ),
    truthInLendingDisclosure: (
      <TermLink
        key='til-disclosure'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK)
          setDocumentIdToShow(loanAgreementDocumentId, true)
        }}
      />
    ),
    loanAgreement: (
      <TermLink
        key='til-loan-agreement'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='loan-agreement-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_LOAN_AGREEMENT_LINK)
          setDocumentIdToShow(loanAgreementDocumentId)
        }}
      />
    ),
    creditScoreDisclosure: (
      <TermLink
        key='til-credit-score-disclosure'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='credit-score-disclosure-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_CREDIT_SCORE_DISCLOSURE_LINK)
          setDocumentIdToShow(creditScoreDisclosureDocumentId)
        }}
      />
    ),
    cardAccountAgreement: (
      <TermLink
        key='til-card-account-agreement'
        href={t('consumerLending:til:cardAccountAgreementUrl')}
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='card-account-agreement-link'
        target='_blank'
      />
    ),
    br: <br />,
  }

  const getTransContext = (): string => {
    if (isSupPMOrder) {
      if (autopayEnabled) {
        return 'supAutopay'
      }
      return 'sup'
    }

    return autopayEnabled ? 'noTilAutopay' : 'noTil'
  }

  const i18nKey = 'consumerLending:til:terms'

  useEffect(() => {
    const termsKey = `${i18nKey}_${getTransContext()}`
    dispatch(setConfirmTermsViewed(termsKey))
  }, [i18nKey, isSupPMOrder, autopayEnabled])

  const context = getTransContext()

  return (
    <div className={designLanguageStyles.termsContainer}>
      <Text color='Gray40' size={isArcadeDesignLanguage ? 'XXS' : 'XS'} fontFace='CashSansMono'>
        <Trans
          key={context}
          i18nKey={i18nKey}
          components={components}
          context={context}
          values={{
            supAmount: formatMoney(orderAmount?.amount),
          }}
        />
      </Text>
    </div>
  )
}
